import {
  HStack,
  IconButton,
  Text,
  Flex,
  Box,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Avatar,
  Divider,
  Button,
  useColorMode,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
} from "@chakra-ui/react";
import {
  Menu as MenuIcon,
  Bell,
  Calendar,
  Moon,
  Sun,
  User,
  LogOut,
  Hexagon,
  Settings,
} from "lucide-react";
import { useLocation, Link, useNavigate } from "react-router-dom";
import { useThemeColors } from "@/hooks/useThemeColors";

interface HeaderProps {
  onOpen: () => void;
  user: { user_login?: string; avatar_url?: string } | null;
  onLogout: () => void;
}

export function Header({ onOpen, user, onLogout }: HeaderProps) {
  const { colorMode, toggleColorMode } = useColorMode();
  const colors = useThemeColors();
  const location = useLocation();
  const navigate = useNavigate();
  const pathname = location.pathname;
  const segments = (pathname || "/").split("/").filter(Boolean);
  const toLabel = (s: string) =>
    s.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  const title = segments.length > 0 ? toLabel(segments[0]) : "Dashboard";
  const crumbs = segments.map((seg: string, idx: number) => ({
    label: toLabel(seg),
    href: "/" + segments.slice(0, idx + 1).join("/"),
  }));

  // Helper function to check if current path matches or starts with given path
  const isActivePath = (path: string) => {
    return location.pathname.startsWith(path);
  };

  return (
    <Box
      bg={colors.cardBg}
      borderBottom="1px"
      borderColor={colors.border}
      px={{ base: 6, md: "31px" }}
      py={{ base: 4, md: "25px" }}
      height={{ base: "auto", md: "83px" }}
    >
      <Flex justify="space-between" align="center">
        <HStack align="start" spacing={3}>
          <IconButton
            aria-label="Open menu"
            icon={<MenuIcon />}
            variant="ghost"
            display={{ base: "flex", md: "none" }}
            onClick={onOpen}
          />
          <Box display={{ base: "none", md: "block" }}>
            <Text
              fontSize="20px"
              fontWeight="semibold"
              fontFamily="Inter, sans-serif"
            >
              {title}
            </Text>
            {segments.length > 0 && (
              <Breadcrumb
                fontSize="sm"
                color={colors.text}
                separator="/"
                fontFamily="Inter, sans-serif"
              >
                {/*<BreadcrumbItem key="home">
                  <BreadcrumbLink as={Link} to="/dashboard">
                    Home
                  </BreadcrumbLink>
                </BreadcrumbItem>
                {crumbs.map((c: { label: string; href: string }, i: number) => (
                  <BreadcrumbItem
                    key={c.href}
                    isCurrentPage={i === crumbs.length - 1}
                  >
                    {i === crumbs.length - 1 ? (
                      <Text>{c.label}</Text>
                    ) : (
                      <BreadcrumbLink as={Link} to={c.href}>
                        {c.label}
                      </BreadcrumbLink>
                    )}
                  </BreadcrumbItem> 
                ))}*/}
              </Breadcrumb>
            )}
          </Box>
        </HStack>

        {/* Action buttons */}
        <HStack spacing={1} align="center">
          {/* <IconButton
            aria-label="Calendar"
            icon={
              <Box
                position="relative"
                display="inline-flex"
                alignItems="center"
                justifyContent="center"
                width="32px"
                height="32px"
                borderRadius="full"
                bg={isActivePath("/calendar") ? "#DC3611" : "white"}
                border="1px solid"
                borderColor={isActivePath("/calendar") ? "#DC3611" : "#E7E8EB"}
              >
                <Calendar
                  size={16}
                  color={isActivePath("/calendar") ? "white" : "#0A1B39"}
                />
              </Box>
            }
            variant="ghost"
            size="md"
            _hover={{ bg: "transparent" }}
            _active={{ bg: "transparent" }}
            onClick={() => navigate("/calendar")}
          />

          <IconButton
            aria-label="Settings"
            icon={
              <Box
                position="relative"
                display="inline-flex"
                alignItems="center"
                justifyContent="center"
                width="32px"
                height="32px"
                borderRadius="full"
                bg={isActivePath("/settings") ? "#DC3611" : "white"}
                border="1px solid"
                borderColor={isActivePath("/settings") ? "#DC3611" : "#E7E8EB"}
              >
                <Box position="relative" display="inline-flex">
                  <Hexagon
                    size={16}
                    color={isActivePath("/settings") ? "white" : "#0A1B39"}
                  />
                  <Box
                    position="absolute"
                    top="50%"
                    left="50%"
                    transform="translate(-50%, -50%)"
                    width="4px"
                    height="4px"
                    borderRadius="full"
                    bg={isActivePath("/settings") ? "white" : "#0A1B39"}
                  />
                </Box>
              </Box>
            }
            variant="ghost"
            size="md"
            _hover={{ bg: "transparent" }}
            _active={{ bg: "transparent" }}
            onClick={() => navigate("/settings")}
          /> */}

          <IconButton
            aria-label="Notifications"
            icon={
              <Box
                position="relative"
                display="inline-flex"
                alignItems="center"
                justifyContent="center"
                width="32px"
                height="32px"
                borderRadius="full"
                bg={isActivePath("/notifications") ? "#DC3611" : "white"}
                border="1px solid"
                borderColor={
                  isActivePath("/notifications") ? "#DC3611" : "#E7E8EB"
                }
              >
                <Bell
                  size={16}
                  color={isActivePath("/notifications") ? "white" : "#0A1B39"}
                />
                <Box
                  position="absolute"
                  top="6px"
                  right="8px"
                  width="6px"
                  height="6px"
                  borderRadius="full"
                  bg={isActivePath("/notifications") ? "white" : "red.500"}
                />
              </Box>
            }
            variant="ghost"
            size="md"
            _hover={{ bg: "transparent" }}
            _active={{ bg: "transparent" }}
            onClick={() => navigate("/notifications")}
          />

          {/* Dark Mode  */}
          {/* <IconButton
            aria-label="Toggle color mode"
            icon={
              colorMode === "light" ? <Moon size={20} /> : <Sun size={20} />
            }
            variant="ghost"
            size="md"
            onClick={toggleColorMode}
            color="gray.600"
            _dark={{ color: "gray.300" }}
          /> */}

          <Menu>
            <MenuButton as={Button} variant="unstyled" size="sm" px={2}>
              <Box position="relative" display="inline-flex">
                <Avatar
                  size="sm"
                  name={user?.user_login || "User"}
                  bg="orange.400"
                  src={user?.avatar_url}
                />
                <Box
                  position="absolute"
                  bottom="0"
                  right="0"
                  width="10px"
                  height="10px"
                  borderRadius="full"
                  bg="green.400"
                  border="2px solid white"
                  _dark={{ borderColor: "gray.800" }}
                />
              </Box>
            </MenuButton>
            <MenuList>
              <MenuItem
                icon={<User />}
                as={Link}
                to="/profile"
                bg={isActivePath("/profile") ? "#DC3611" : "transparent"}
                color={isActivePath("/profile") ? "white" : "inherit"}
                _hover={{
                  bg: isActivePath("/profile") ? "#DC3611" : "gray.100",
                  color: isActivePath("/profile") ? "white" : "inherit",
                }}
                iconSpacing={2}
              >
                Profile
              </MenuItem>
              <Divider />
              <MenuItem icon={<LogOut size={16} />} onClick={onLogout}>
                Logout
              </MenuItem>
            </MenuList>
          </Menu>
        </HStack>
      </Flex>
    </Box>
  );
}

export default Header;
